// `BoardFacts` from a `GameState` (PLAN-CLIENT-SPLIT §2.1).
//
// **Every square in ascending order, every optional layer only when it is
// there.** Two clients drawing one board must build the same map, so the sweep
// is a sorted union of every square-keyed store — `hash.ts`'s discipline — and
// a field that is absent says so by being absent, never by a sentinel.
//
// Built once per viewed board and memoised by `createGame`; nothing here is
// cheap enough to run per frame and nothing needs to.

import type {
  BeltFacts,
  BoardFacts,
  DepositFacts,
  Direction,
  EquipmentFacts,
  Fixture,
  HeapFacts,
  HistoryFacts,
  PieceFacts,
  Side,
  Square,
  SquareFacts,
  Status,
  StructureFacts,
  TrainFacts,
  TurretFacts,
  UnitFacts
} from "src/api";
import { type Direction as Dir, PAWN, pieceColor, pieceType, type PieceType } from "src/engine/rules/board";
import { threatenedRoyals } from "src/engine/rules/chess/attacks";
import type { GameState } from "src/engine/rules/commands";
import {
  beltOnAt,
  beltOwnerAt,
  blueprintDirAt,
  blueprintOwnerAt,
  cookingCount,
  cookingUnitAt,
  depositAt,
  depositYieldAt,
  depositContents,
  depositNameAt,
  depositTierAt,
  droppedAt,
  type Gear,
  heapAt,
  heapRankAt,
  heapSideAt,
  type IndustryState,
  type Structure,
  type Train,
  TURRET,
  KINETIC_TURRET,
  CANNON_TURRET,
  liveSquares,
  NO_BELT,
  beltDirAt,
  BATTERY,
  structureAt,
  unitAt,
  unitCount,
  unitGrade,
  unitLine,
  unitValue
} from "src/engine/rules/industry/state";
import { structureHealthOf } from "src/engine/rules/industry/structures";
import { ammoOf, fieldSquares, gunOf, turretEmplaced } from "src/engine/rules/industry/turret";
import { targetingArrays } from "src/engine/rules/campaign/powers";
import { DEPOSIT_TIERS, materialId, materialName, type MaterialLine } from "src/engine/rules/materials";
import {
  areEnemies,
  disguisedAs,
  facingAt,
  healthOf,
  livingSides,
  maxHealthOf,
  platingAt,
  pieceIdAt,
  type Position,
  promotionOwnerAt,
  variantAt
} from "src/engine/rules/position";
import { RULES } from "src/engine/rules/rules";
import { gearBorneAt, gearLyingAt, gearName, gearPowered } from "src/engine/rules/campaign/equipment";
import { seizedBy } from "src/engine/rules/campaign/scepter";
import { ZONE_KEY, type ZoneKind } from "src/shared/zones";
import { armyFacing } from "./armyFacing";
import { beltShapeAt } from "./beltShape";
import { levelBounds } from "./framing";
import { asSide, asSquare, BUILDING_NAME, DIRECTION_NAME, LINE_NAME, PIECE_NAME, STRUCTURE_NAME, VARIANT_NAME } from "./names";
import { tempoOf } from "./tempo";

/**
 * Cheapest first, **by value rather than by rung**, since a pile can hold two
 * lines and a rung means nothing across them; ties by the word, so the order
 * is stable and a pile does not reshuffle when a unit arrives.
 */
const byWorth = (a: number, b: number): number => unitValue(a) - unitValue(b) || a - b;

function unitFacts(pos: Position, unit: number, where: UnitFacts["where"]): UnitFacts {
  const line = unitLine(unit) as MaterialLine;
  const grade = unitGrade(unit);
  return {
    line: LINE_NAME[line],
    grade,
    name: materialName(line, grade),
    where,
    material: materialId(line, grade)
  };
}

/** Sorted cheapest first. */
function unitsOn(pos: Position, units: readonly [unit: number, where: UnitFacts["where"]][]): UnitFacts[] {
  return [...units].sort((a, b) => byWorth(a[0], b[0])).map(([unit, where]) => unitFacts(pos, unit, where));
}

/**
 * Only pawns (and the odd Pattern link) store a facing; absent reads as north,
 * which is right for a pawn and wrong for a Black rook. Anything else faces
 * where it last moved (`Position.heading`), and until it has, the way its army
 * does, so the two back ranks face each other.
 */
function pieceFacing(pos: Position, sq: number, piece: number, army: (side: number) => Dir): Dir {
  if (pieceType(piece) === PAWN || pos.facing.has(sq)) return facingAt(pos, sq);
  return pos.heading.get(pieceIdAt(pos, sq)) ?? army(pieceColor(piece));
}

function equipmentFacts(industry: IndustryState, gear: readonly Gear[]): EquipmentFacts[] {
  return gear.map((g) => ({
    name: gearName(g.kind),
    artifact: STRUCTURE_NAME[g.kind],
    origin: asSide(g.origin),
    powered: gearPowered(industry, g)
  }));
}

function pieceFacts(
  pos: Position,
  industry: IndustryState,
  sq: number,
  army: (side: number) => Dir
): PieceFacts | undefined {
  const piece = pos.board.get(sq);
  if (!piece) return undefined;
  const worn = disguisedAs(pos, sq);
  const borne = gearBorneAt(pos, industry, sq);
  return {
    id: pieceIdAt(pos, sq),
    type: PIECE_NAME[pieceType(piece) as PieceType],
    side: asSide(pieceColor(piece)),
    facing: DIRECTION_NAME[pieceFacing(pos, sq, piece, army)],
    variant: VARIANT_NAME[variantAt(pos, sq)],
    health: { now: healthOf(pos, sq), max: maxHealthOf(pos, sq), plating: platingAt(pos, sq) },
    ...(worn ? { disguisedAs: { type: PIECE_NAME[worn.rank], side: asSide(worn.side) } } : {}),
    ...(borne.length > 0 ? { equipment: equipmentFacts(industry, borne) } : {})
  };
}

function beltFacts(industry: IndustryState, sq: number): BeltFacts | undefined {
  const shape = beltShapeAt(industry, sq);
  if (!shape) return undefined;
  const built = beltDirAt(industry, sq);
  const blueprint = built === NO_BELT;
  const dir = blueprint ? blueprintDirAt(industry, sq) : built;
  const owner = blueprint ? blueprintOwnerAt(industry, sq) : beltOwnerAt(industry, sq);
  return {
    dir: DIRECTION_NAME[dir as Dir],
    owner: asSide(owner),
    on: !blueprint && beltOnAt(industry, sq) === 1,
    blueprint,
    bend: shape.bend,
    feeders: shape.feeders.map((d) => DIRECTION_NAME[d])
  };
}

function structureFacts(pos: Position, industry: IndustryState, sq: number): StructureFacts | undefined {
  const structure = structureAt(industry, sq);
  if (!structure) return undefined;
  return {
    kind: STRUCTURE_NAME[structure.kind],
    building: BUILDING_NAME[structure.kind],
    ...(structure.kind === BATTERY ? { charge: structure.charge ?? 0 } : {}),
    ...(structure.train ? { train: trainFacts(structure.train) } : {}),
    ...(structure.turret ? { turret: turretFacts(pos, industry, sq, structure) } : {}),
    owner: asSide(structure.owner),
    health: structureHealthOf(structure),
    stock: unitsOn(
      pos,
      structure.stock.map((u) => [u, "held"] as const)
    )
  };
}

const GUN_NAME: Record<number, TurretFacts["gun"]> = { [TURRET]: "laser", [KINETIC_TURRET]: "kinetic", [CANNON_TURRET]: "cannon" };

function turretFacts(pos: Position, industry: IndustryState, sq: number, structure: Structure): TurretFacts {
  const turret = structure.turret!;
  const arrays = targetingArrays(industry, structure.owner);
  return {
    gun: GUN_NAME[structure.kind] ?? "laser",
    ...(gunOf(structure.kind).shape === "cone" ? { facing: DIRECTION_NAME[turret.facing as Dir] } : {}),
    shots: turret.shots,
    fuel: turret.fuel,
    ammo: ammoOf(structure),
    emplaced: turretEmplaced(structure),
    field: fieldSquares(pos, sq, structure, arrays).map(asSquare)
  };
}

function trainFacts(train: Train): TrainFacts {
  const heading = train.heading ?? -1;
  const dest = train.dest ?? -1;
  return {
    ...(heading >= 0 ? { heading: DIRECTION_NAME[heading as Dir] } : {}),
    ...(dest >= 0 ? { dest: asSquare(dest) } : {}),
    ...(train.cars ? { cars: train.cars.map(asSquare) } : {}),
    ...(train.riders
      ? {
          riders: train.riders.map((one) => ({
            id: one.pieceId,
            type: PIECE_NAME[pieceType(one.piece) as PieceType],
            side: asSide(pieceColor(one.piece)),
            variant: VARIANT_NAME[one.variant]
          }))
        }
      : {})
  };
}

function heapFacts(industry: IndustryState, sq: number): HeapFacts | undefined {
  const size = heapAt(industry, sq);
  if (size <= 0) return undefined;
  return {
    size,
    wreck: PIECE_NAME[(heapRankAt(industry, sq) || PAWN) as PieceType],
    side: asSide(Math.max(0, heapSideAt(industry, sq)))
  };
}

function depositFacts(pos: Position, industry: IndustryState, sq: number): DepositFacts | undefined {
  const left = depositAt(industry, sq);
  if (left <= 0) return undefined;
  const contents = depositContents(industry, sq).map(({ unit, count }) => ({
    unit: unitFacts(pos, unit, "loose"),
    count
  }));
  const tier = DEPOSIT_TIERS[depositTierAt(industry, sq)].word;
  return {
    yield: unitFacts(pos, depositYieldAt(industry, sq), "loose"),
    name: depositNameAt(industry, sq),
    ...(tier ? { tier: tier as "thin" | "concentrated" | "rich" } : {}),
    contents,
    dominant: contents.reduce((best, one) => (one.count > best.count ? one : best)).unit,
    left,
    fraction: Math.min(1, left / RULES.depositAmountSeed)
  };
}

function unitsAt(pos: Position, industry: IndustryState, sq: number): UnitFacts[] {
  const units: [number, UnitFacts["where"]][] = [];
  for (let i = 0; i < unitCount(industry, sq, "cargo"); i++) units.push([unitAt(industry, sq, i, "cargo"), "belt"]);
  for (const bank of ["in", "out"] as const) {
    for (let i = 0; i < unitCount(industry, sq, bank); i++) units.push([unitAt(industry, sq, i, bank), "held"]);
  }
  for (let i = 0; i < cookingCount(industry, sq); i++) units.push([cookingUnitAt(industry, sq, i), "cooking"]);
  for (const unit of droppedAt(industry, sq)) units.push([unit, "loose"]);
  return unitsOn(pos, units);
}

export function squareFacts(
  state: GameState,
  sq: number,
  threatened: ReadonlySet<number> = new Set(threatenedRoyals(state.position, state.position.turn)),
  army: (side: number) => Dir = (side) => armyFacing(state.position, side)
): SquareFacts {
  const { position: pos, industry } = state;
  const piece = pieceFacts(pos, industry, sq, army);
  const zone = industry.zone?.get(sq);
  const lying = gearLyingAt(industry, sq);
  const belt = beltFacts(industry, sq);
  const structure = structureFacts(pos, industry, sq);
  const heap = heapFacts(industry, sq);
  const deposit = depositFacts(pos, industry, sq);
  // A marker that promotes nobody (a run, §31 P2) is not drawn as one.
  const owner = pos.promotion.has(sq) && !pos.runPawns ? promotionOwnerAt(pos, sq) : undefined;
  return {
    square: asSquare(sq),
    cell: pos.cells.has(sq) ? "board" : structure ? "structure-floor" : "none",
    ...(piece ? { piece } : {}),
    ...(belt ? { belt } : {}),
    ...(structure ? { structure } : {}),
    ...(heap ? { heap } : {}),
    ...(deposit ? { deposit } : {}),
    units: unitsAt(pos, industry, sq),
    ...(threatened.has(sq) ? { royalThreatened: true } : {}),
    ...(owner !== undefined ? { promotion: owner < 0 ? "anyone" : asSide(owner) } : {}),
    ...(zone ? { zone: ZONE_KEY[zone as ZoneKind] } : {}),
    ...(lying.length > 0 ? { gear: equipmentFacts(industry, lying) } : {})
  };
}

/** Every square with ground or something on it, ascending. */
function factSquares(state: GameState): number[] {
  const { position: pos, industry } = state;
  const live = new Set(
    liveSquares(
      pos.board,
      pos.promotion,
      industry.heap,
      industry.beltDir,
      industry.blueprintDir,
      industry.deposit,
      industry.beltCargo,
      industry.dropped,
      industry.structures
    )
  );
  for (const sq of pos.cells) live.add(sq);
  return [...live].sort((a, b) => a - b);
}

/**
 * Is a run still in its opening — nobody on the board to fight?
 *
 * Three readings existed (the fly-in latch's "no move yet", the spawn clock's
 * "unarmed", and the board's own living sides); this is the board's, because
 * it is the one that stays true through a resumed run's first frame.
 */
function opening(state: GameState): boolean {
  const run = state.run;
  if (!run) return false;
  for (const side of livingSides(state.position)) {
    if (areEnemies(state.position, run.side, side)) return false;
  }
  return true;
}

/** `mover` is the side that just moved — the pace is theirs, not the side to move's. */
export function boardFacts(state: GameState, mover: number = state.position.turn): BoardFacts {
  const pos = state.position;
  const threatened = new Set(threatenedRoyals(pos, pos.turn));
  const armies = new Map<number, Dir>();
  const army = (side: number): Dir => {
    let dir = armies.get(side);
    if (dir === undefined) armies.set(side, (dir = armyFacing(pos, side)));
    return dir;
  };
  const squares = new Map<Square, SquareFacts>();
  for (const sq of factSquares(state)) squares.set(asSquare(sq), squareFacts(state, sq, threatened, army));

  const facing = new Map<Side, Direction>();
  for (const side of livingSides(pos)) facing.set(asSide(side), DIRECTION_NAME[army(side)]);

  const run = state.run;
  const kingAt = run ? pos.kings[run.side] : -1;
  return {
    squares,
    bounds: { ...pos.bounds },
    framed: levelBounds(pos.bounds, run?.level),
    armyFacing: facing,
    tempo: tempoOf(state, mover),
    ...(run
      ? { run: { side: asSide(run.side), ...(kingAt >= 0 ? { kingAt: asSquare(kingAt) } : {}), opening: opening(state) } }
      : {})
  };
}

export function statusOf(state: GameState, viewing: number | null): Status {
  const { position: pos, status, run } = state;
  const over =
    status === "checkmate" || status === "stalemate" || (!run && status.startsWith("draw-"));
  return {
    turn: asSide(pos.turn),
    sides: pos.sides,
    ply: pos.ply,
    turns: state.industry.turnCounter,
    movesLeft: pos.movesLeft,
    fodderLeft: pos.fodderLeft,
    round: pos.round,
    movesSpent: pos.turnMoves,
    forged: pos.turnForged,
    check: status === "check",
    outcome: status,
    over,
    ...(run
      ? {
          run: {
            ...(run.level ? { level: { id: run.level.levelId, name: run.level.levelName } } : {}),
            ...(state.stage !== undefined ? { stage: state.stage } : {})
          }
        }
      : {}),
    ...(seizedBy(state) !== undefined ? { seizedBy: asSide(seizedBy(state)!) } : {}),
    viewing
  };
}

export function fixtureOf(facts: BoardFacts, status: Status): Fixture {
  return {
    squares: [...facts.squares.values()],
    bounds: facts.bounds,
    framed: facts.framed,
    armyFacing: [...facts.armyFacing.entries()],
    tempo: facts.tempo,
    ...(facts.run ? { run: facts.run } : {}),
    status
  };
}

export type { HistoryFacts };
